import React, {
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  Grid,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LoadingScreen from "../components/LoadingScreen";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import IconButton from "@mui/material/IconButton";
import excelIcon from "../assets/icons/excel_export.png";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import DiamondRoundedIcon from "@mui/icons-material/DiamondRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";

const PAGE_SIZE = 20;

/* ─── Blue/White design tokens (shared with the rest of the app) ─── */
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const BG_ALT = "#F8FAFC";

// MySQL hands the flags back as 0/1 (sometimes as strings), so they are coerced
// before being read as booleans.
const isPawnStore = (store) => Boolean(Number(store?.is_pawn));
const isTravelStore = (store) => Boolean(Number(store?.is_travel));

// store_master.common_store is the group id: the store's own id when it heads a
// group, the parent's id when it belongs to one, null when it is standalone.
const groupIdOf = (store) =>
  store?.common_store === null || store?.common_store === undefined
    ? null
    : String(store.common_store);

const relationOf = (store) => {
  const groupId = groupIdOf(store);
  if (groupId === null) return null;
  return groupId === String(store?.store_id) ? "parent" : "child";
};

const TABS = [
  { value: "all", label: "All Stores", icon: GridViewRoundedIcon },
  { value: "normal", label: "Normal Stores", icon: StorefrontRoundedIcon },
  {
    value: "connected",
    label: "Connected Stores",
    icon: AccountTreeRoundedIcon,
  },
  { value: "travel", label: "Travel ERPs", icon: FlightTakeoffRoundedIcon },
  { value: "pawn", label: "Pawn Stores", icon: DiamondRoundedIcon },
];

// Tabs are independent filters, not a partition — a pawn store that belongs to a
// group is counted under both "Pawn Stores" and "Connected Stores", and every
// store is counted under "All Stores".
const matchesTab = (store, tab) => {
  switch (tab) {
    case "all":
      return true;
    case "connected":
      return groupIdOf(store) !== null;
    case "travel":
      return isTravelStore(store);
    case "pawn":
      return isPawnStore(store);
    default:
      return !isPawnStore(store) && !isTravelStore(store);
  }
};

const BADGES = {
  pawn: { label: "Pawn", fg: "#6D28D9", bg: "#F5F3FF", br: "#DDD6FE" },
  travel: { label: "Travel ERP", fg: "#0F766E", bg: "#F0FDFA", br: "#99F6E4" },
  parent: { label: "Parent", fg: BLUE_DARK, bg: BLUE_PALE, br: "#BFDBFE" },
  child: { label: "Child", fg: INK_MUTED, bg: "#F1F5F9", br: BORDER },
};

// Pawn and travel stores also get a coloured edge on the card so they are
// spottable at a glance in the mixed "All Stores" list.
const accentOf = (store) => {
  if (isPawnStore(store)) return BADGES.pawn.fg;
  if (isTravelStore(store)) return BADGES.travel.fg;
  return null;
};

// A store's branch count, straight off the row the API serves.
const branchCountOf = (store) =>
  Number(store?.branch_count ?? store?.branches?.length ?? 0);

const badgesOf = (store) => {
  const out = [];
  if (isPawnStore(store)) out.push(BADGES.pawn);
  if (isTravelStore(store)) out.push(BADGES.travel);
  const relation = relationOf(store);
  if (relation) out.push(BADGES[relation]);
  // Only worth saying when there is more than one place — every store has at
  // least a main branch, so "1 branch" is noise on every single card.
  const branches = branchCountOf(store);
  if (branches > 1)
    out.push({
      label: `${branches} branches`,
      fg: "#B45309",
      bg: "#FFFBEB",
      br: "#FDE68A",
    });
  return out;
};

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "?";

/* ─────────────────────────── Sub-components ─────────────────────────── */

const FilterTab = ({ tab, count, active, onClick }) => {
  const Icon = tab.icon;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexShrink: 0,
        px: { xs: 1.1, sm: 1.35 },
        py: 0.7,
        borderRadius: 2,
        cursor: "pointer",
        userSelect: "none",
        border: `1px solid ${active ? "transparent" : BORDER}`,
        background: active
          ? "linear-gradient(90deg, #102A83 0%, #1D4ED8 100%)"
          : SURFACE,
        boxShadow: active
          ? "0 4px 12px rgba(29,78,216,0.28)"
          : "0 1px 2px rgba(15,23,42,0.04)",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: active ? "transparent" : "#BFDBFE",
          bgcolor: active ? undefined : BLUE_PALE,
        },
        "& svg": {
          fontSize: 15,
          color: active ? "#fff" : INK_MUTED,
        },
      }}
    >
      <Icon />
      <Typography
        noWrap
        sx={{
          fontSize: { xs: "0.72rem", sm: "0.76rem" },
          fontWeight: active ? 700 : 600,
          color: active ? "#fff" : INK_SOFT,
        }}
      >
        {tab.label}
      </Typography>
      <Box
        sx={{
          minWidth: 20,
          px: 0.55,
          py: 0.05,
          borderRadius: 5,
          textAlign: "center",
          bgcolor: active ? "rgba(255,255,255,0.22)" : BLUE_PALE,
          border: `1px solid ${active ? "rgba(255,255,255,0.35)" : "#DBEAFE"}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.66rem",
            fontWeight: 700,
            color: active ? "#fff" : BLUE_DARK,
          }}
        >
          {count}
        </Typography>
      </Box>
    </Box>
  );
};

const MetaLine = ({ icon, children, clamp = 1 }) => (
  <Box
    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}
  >
    <Box
      sx={{
        display: "flex",
        mt: "1px",
        "& svg": { fontSize: 13, color: INK_MUTED },
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: "0.71rem",
        color: INK_SOFT,
        lineHeight: 1.35,
        display: "-webkit-box",
        WebkitLineClamp: clamp,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
      }}
    >
      {children}
    </Typography>
  </Box>
);

// Compact card — 4 per row on desktop, 2 on mobile, so every card keeps the
// same height regardless of how much address text it carries.
const StoreCard = ({ store, onClick, isMobile }) => {
  const accent = accentOf(store);

  return (
    <Tooltip
      title="Click to edit"
      arrow
      placement="top"
      disableHoverListener={isMobile}
      enterDelay={600}
    >
      <Box
        onClick={onClick}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 0.85,
          height: "100%",
          p: { xs: 1.1, sm: 1.35 },
          pl: accent ? { xs: 1.35, sm: 1.6 } : { xs: 1.1, sm: 1.35 },
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          cursor: "pointer",
          transition: "all 0.2s ease",
          // Left edge stripe — drawn as a pseudo-element so a store's type never
          // shifts the card's content.
          ...(accent && {
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: 3,
              bgcolor: accent,
            },
          }),
          "&:hover": {
            borderColor: "#BFDBFE",
            boxShadow: {
              xs: "0 1px 2px rgba(15,23,42,0.04)",
              md: "0 8px 20px rgba(15,23,42,0.10)",
            },
            transform: { xs: "none", md: "translateY(-2px)" },
          },
        }}
      >
        {/* Logo + name */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              minWidth: 38,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundImage: store?.image
                ? `url(${store.image})`
                : "linear-gradient(135deg, #FBBF24, #B45309)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.08)",
            }}
          >
            {!store?.image && (
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 800,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                {initialsOf(store?.store_name)}
              </Typography>
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.79rem",
                fontWeight: 700,
                color: INK,
                lineHeight: 1.25,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {store?.store_name || "--"}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: "0.66rem", fontWeight: 600, color: INK_MUTED }}
            >
              #{store?.store_id}
              {store?.branch_id ? ` · ${store.branch_id}` : ""}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: "1px", bgcolor: BORDER }} />

        {/* Address + owner */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
          <MetaLine icon={<LocationOnRoundedIcon />} clamp={2}>
            {store?.address || "--"}
          </MetaLine>
          <MetaLine icon={<PersonRoundedIcon />}>
            {store?.head_name || "--"}
          </MetaLine>
        </Box>

        {/* Badges pinned to the bottom so cards in a row stay aligned */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            mt: "auto",
            pt: 0.25,
          }}
        >
          {badgesOf(store).map((badge) => (
            <Box
              key={badge.label}
              sx={{
                px: 0.6,
                py: 0.1,
                borderRadius: 1,
                bgcolor: badge.bg,
                border: `1px solid ${badge.br}`,
              }}
            >
              <Typography
                sx={{ fontSize: "0.62rem", fontWeight: 700, color: badge.fg }}
              >
                {badge.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
};

const EmptyState = ({ message, hint }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.75,
      width: "100%",
      py: { xs: 5, md: 7 },
      borderRadius: 3,
      border: `1px dashed ${BORDER}`,
      bgcolor: BG_ALT,
      "& svg": { fontSize: 34, color: "#94A3B8" },
    }}
  >
    <SearchOffRoundedIcon />
    <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: INK_SOFT }}>
      {message}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const cardSpan = { xs: 6, sm: 6, md: 4, lg: 3 };

function StoreDirectory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();
  const { allStores = [] } = useContext(AutoTaskAuthContext);

  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Sentinel ref — the invisible div at the bottom of the list
  const sentinelRef = useRef(null);

  // The screen owns its own scrolling (see the scroll box in the markup), so it
  // has to know how much height is left below the app header. Measuring the
  // box's own offset picks up the header, the safe-area insets and main's
  // padding on its own — no calc(100vh - guess) to keep in sync.
  const scrollRef = useRef(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      const offsetTop = el.getBoundingClientRect().top + window.scrollY;
      // Mobile keeps the fixed bottom nav clear; desktop only the 8px shell pad.
      const bottomReserve = isMobile ? 76 : 8;
      setScrollAreaHeight(
        Math.max(320, window.innerHeight - offsetTop - bottomReserve)
      );
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [isMobile]);

  // Search runs before the tabs so the tab counts always describe what a click
  // would actually show.
  const searchedStores = useMemo(() => {
    const trimmed = searchText.trim();
    if (!trimmed) return [...allStores];
    if (!isNaN(trimmed)) {
      return allStores.filter(
        (store) => Number(store?.store_id) === Number(trimmed)
      );
    }
    const needle = trimmed.toLowerCase();
    return allStores.filter(
      (store) =>
        store?.store_name?.trim().toLowerCase().includes(needle) ||
        // Any branch, not just the main one: a store is searched for by
        // whichever of its places the caller is actually asking about.
        (store?.branches || []).some(
          (branch) =>
            branch?.code?.trim().toLowerCase().includes(needle) ||
            branch?.name?.trim().toLowerCase().includes(needle) ||
            branch?.address?.trim().toLowerCase().includes(needle)
        )
    );
  }, [allStores, searchText]);

  const tabCounts = useMemo(() => {
    const counts = {};
    TABS.forEach((tab) => {
      counts[tab.value] = searchedStores.filter((store) =>
        matchesTab(store, tab.value)
      ).length;
    });
    return counts;
  }, [searchedStores]);

  const filteredStores = useMemo(() => {
    const rows = searchedStores.filter((store) => matchesTab(store, activeTab));
    if (activeTab !== "connected") return rows;
    // Keep every group's members next to each other, parent first, so slicing
    // for infinite scroll never interleaves two groups.
    return rows.sort((a, b) => {
      const groupDiff = Number(groupIdOf(a)) - Number(groupIdOf(b));
      if (groupDiff !== 0) return groupDiff;
      const relDiff =
        (relationOf(a) === "parent" ? 0 : 1) -
        (relationOf(b) === "parent" ? 0 : 1);
      if (relDiff !== 0) return relDiff;
      return String(a?.store_name || "").localeCompare(
        String(b?.store_name || "")
      );
    });
  }, [searchedStores, activeTab]);

  const visibleStores = filteredStores.slice(0, visibleCount);
  const hasMore = visibleCount < filteredStores.length;

  // Groups are built from the visible slice only, so a long group simply grows
  // as more pages load instead of blocking the first paint.
  const visibleGroups = useMemo(() => {
    if (activeTab !== "connected") return [];
    const byGroup = new Map();
    visibleStores.forEach((store) => {
      const groupId = groupIdOf(store);
      if (!byGroup.has(groupId)) byGroup.set(groupId, []);
      byGroup.get(groupId).push(store);
    });
    return [...byGroup.entries()].map(([groupId, members]) => {
      const parent = members.find((m) => relationOf(m) === "parent") || null;
      // The parent can be filtered out by the search while its children match,
      // so its name is looked up from the full list for the group heading.
      const head =
        parent ||
        allStores.find((store) => String(store?.store_id) === groupId) ||
        null;
      return {
        groupId,
        members,
        headName: head?.store_name || `Group ${groupId}`,
      };
    });
  }, [activeTab, visibleStores, allStores]);

  // Reset paging whenever the result set changes underneath it
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchText, activeTab]);

  // IntersectionObserver to load more when sentinel is visible
  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    // Small timeout gives browser a frame to paint current items first
    setTimeout(() => {
      setVisibleCount((prev) => prev + PAGE_SIZE);
      setIsLoadingMore(false);
    }, 300);
  }, [hasMore, isLoadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      // The list scrolls inside our own box, so that box is the root.
      { root: scrollRef.current, rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const openStore = (store) =>
    navigate("/create-store", {
      state: {
        ...store,
        is_pawn: isPawnStore(store),
        is_travel: isTravelStore(store),
      },
    });

  // Excel export — always the full store list, regardless of the active filter
  const handleExportStores = () => {
    try {
      if (!allStores || allStores.length === 0) return;

      // One row per BRANCH, not per store: address, server IP and the
      // withstanding amount differ between a store's places, so a store-level
      // row would have to pick one of them and quietly drop the rest.
      const formattedData = allStores.flatMap((store) => {
        const storeColumns = {
          "Store ID": store.store_id,
          "Store Name": store.store_name,
          "Store Type": isPawnStore(store)
            ? "Pawn Store"
            : isTravelStore(store)
            ? "Travel ERP"
            : "Normal Store",
          Connection:
            relationOf(store) === "parent"
              ? "Parent"
              : relationOf(store) === "child"
              ? "Child"
              : "Standalone",
          "GST Number": store.gst_number,
          "Owner Name": store.head_name,
          Mobile: store.head_phone,
          Email: store.head_email,
          Status: store.status,
        };

        const branches = store.branches?.length ? store.branches : [null];

        return branches.map((branch) => ({
          ...storeColumns,
          "Branch Name": branch?.name ?? "",
          "Branch Code": branch?.code ?? "",
          "Main Branch": branch?.is_main ? "Yes" : "No",
          Address: branch?.address ?? "",
          "Location URL": branch?.location_url ?? "",
          Amount: branch?.amount ?? "",
          "Server IP": branch?.server_ip ?? "",
          "AnyDesk ID": branch?.anydesk_id ?? "",
          "UltraViewer ID": branch?.ultraview_id ?? "",
        }));
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Store Branches");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, "Store_Branch_Data.xlsx");
    } catch (error) {
      console.error(error);
    }
  };

  const activeTabLabel =
    TABS.find((tab) => tab.value === activeTab)?.label || "Stores";

  return (
    <>
      {/* ── Scroll area ─────────────────────────────────────────────────────
          The list scrolls in here rather than in the page, which is what makes
          the toolbar below actually stick: App.js gives <main> overflowY:auto
          without constraining its height, so main is a scroll container that
          never scrolls — position:sticky inside it can never engage. Owning the
          scroll here fixes it for this screen without touching the app shell.
          The side margins undo main's mobile padding and re-add it inside, so
          the sticky toolbar can span the full width while cards stay inset. */}
      <Box
        ref={scrollRef}
        sx={{
          height: scrollAreaHeight ? `${scrollAreaHeight}px` : "auto",
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          mx: { xs: -2, md: 0 },
          px: { xs: 2, md: 0 },
          /* hide scrollbar but keep scrolling */
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* ── Toolbar: search · filter tabs · export ─────────────────────────
            Desktop keeps all three on one row; mobile puts the tabs on their
            own row above the search + export pair.

            Sticky to the top of the scroll area so search and the filter tabs
            stay reachable however far down the list you are. The negative side
            margins let the opaque background span the mobile padding,
            otherwise cards would slide past in the gutters. */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            bgcolor: "background.default",
            borderBottom: `1px solid ${BORDER}`,
            mx: { xs: -2, md: 0 },
            px: { xs: 2, md: 0 },
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: { xs: 1, md: 1.25 },
            width: { xs: "auto", md: "100%" },
            pt: 2,
            pb: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              order: { xs: 2, md: 1 },
              flex: { xs: 1, md: "0 0 auto" },
              width: { md: 250 },
              minWidth: 0,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or ID"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={searchSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: INK_MUTED }} />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchText("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseRoundedIcon
                        sx={{ fontSize: 15, color: INK_MUTED }}
                      />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          <Box
            sx={{
              order: { xs: 1, md: 2 },
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              width: { xs: "100%", md: "auto" },
              flex: { md: 1 },
              minWidth: 0, // lets the strip scroll instead of pushing the row wide
              overflowX: "auto",
              py: 0.25,
              // The tab strip scrolls on narrow screens without showing a bar
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {TABS.map((tab) => (
              <FilterTab
                key={tab.value}
                tab={tab}
                count={tabCounts[tab.value] || 0}
                active={activeTab === tab.value}
                onClick={() => setActiveTab(tab.value)}
              />
            ))}
          </Box>

          <Box sx={{ order: 3, display: "flex" }}>
            <Tooltip title="Export to Excel" arrow>
              <IconButton
                onClick={handleExportStores}
                sx={{
                  bgcolor: SURFACE,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
                  p: 0.85,
                  "&:hover": { bgcolor: BLUE_PALE, borderColor: "#BFDBFE" },
                }}
              >
                <img
                  src={excelIcon}
                  alt="Export Excel"
                  style={{ width: 22, height: 22, objectFit: "contain" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Stores ───────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
            gap: 1.5,
          }}
        >
          {/* Initial load — the context has not delivered any store yet */}
          {allStores.length === 0 && (
            <LoadingScreen loadingMessage={"Loading stores"} />
          )}

          {allStores.length > 0 && filteredStores.length === 0 && (
            <EmptyState
              message={
                searchText
                  ? "No stores match your search"
                  : activeTab === "all"
                  ? "No stores yet"
                  : `No ${activeTabLabel.toLowerCase()} yet`
              }
              hint={
                searchText
                  ? "Try a different name, branch or store ID"
                  : "Switch to another tab or add a new store"
              }
            />
          )}

          {/* Connected view keeps each group boxed together, parent first */}
          {activeTab === "connected" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                width: "100%",
              }}
            >
              {visibleGroups.map((group) => (
                <Box
                  key={group.groupId}
                  sx={{
                    width: "100%",
                    // Extra padding leaves room for the Grid's negative gutters,
                    // so cards do not sit flush against the group border.
                    p: { xs: 1.5, sm: 1.75 },
                    borderRadius: 3,
                    border: `1px solid ${BORDER}`,
                    bgcolor: BG_ALT,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.85,
                      mb: 1,
                      px: 0.35,
                    }}
                  >
                    <AccountTreeRoundedIcon
                      sx={{ fontSize: 16, color: BLUE_DARK }}
                    />
                    <Typography
                      noWrap
                      sx={{ fontSize: "0.78rem", fontWeight: 700, color: INK }}
                    >
                      {group.headName}
                    </Typography>
                    <Box
                      sx={{
                        px: 0.6,
                        py: 0.1,
                        borderRadius: 1,
                        bgcolor: BLUE_PALE,
                        border: `1px solid #BFDBFE`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          color: BLUE_DARK,
                        }}
                      >
                        {group.members.length} store
                        {group.members.length > 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    {group.members.map((store) => (
                      <Grid item {...cardSpan} key={store?.store_id}>
                        <StoreCard
                          store={store}
                          isMobile={isMobile}
                          onClick={() => openStore(store)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          ) : (
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {visibleStores.map((store) => (
                <Grid item {...cardSpan} key={store?.store_id}>
                  <StoreCard
                    store={store}
                    isMobile={isMobile}
                    onClick={() => openStore(store)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Sentinel element — IntersectionObserver watches this */}
          <Box ref={sentinelRef} sx={{ width: "100%", height: 1 }} />

          {/* Next page on its way in */}
          {isLoadingMore && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 2,
              }}
            >
              <CircularProgress size={18} thickness={4} />
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: INK_MUTED,
                }}
              >
                Loading more stores…
              </Typography>
            </Box>
          )}

          {/* Nothing left to page in */}
          {!isLoadingMore && !hasMore && filteredStores.length > 0 && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: INK_MUTED,
                py: 2,
              }}
            >
              All {filteredStores.length} store
              {filteredStores.length > 1 ? "s" : ""} loaded
            </Typography>
          )}

          {/* Keeps the last row clear of the floating button */}
          <Box sx={{ height: { xs: 72, md: 56 }, width: "100%" }} />
        </Box>
      </Box>

      {/* Add New Store Button */}
      <Box
        onClick={() => navigate("/create-store")}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          background: theme.lightTheme?.header?.home,
          px: 1.75,
          py: 1,
          borderRadius: 50,
          border: "3px solid #d4ddff",
          boxShadow: "0 8px 20px rgba(15,23,42,0.28)",
          position: "fixed",
          bottom: "calc(86px + env(safe-area-inset-bottom, 44px))",
          right: "16px",
          cursor: "pointer",
          transition: "transform 0.2s ease",
          "&:hover": { transform: "translateY(-2px)" },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 17, color: "#fff" }} />
        <Typography sx={{ color: "#fff", fontSize: "0.8rem", fontWeight: 700 }}>
          Add New
        </Typography>
      </Box>
    </>
  );
}

const searchSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    height: 38,
    bgcolor: SURFACE,
    fontSize: "0.8rem",
    color: INK,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#BFDBFE" },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "1.5px" },
  },
  "& input::placeholder": { color: INK_MUTED, opacity: 1 },
};

export default StoreDirectory;
